import { CallDto, UserDto } from '@act/shared/data-transfer-objects';
import { CallStatus } from '@act/shared/definitions';
import { InfoOutlined } from '@mui/icons-material';
import { Typography } from '@mui/material';
import { DateTime } from 'luxon';
import { ReactElement } from 'react';

export const handleConnectionLogEntries = (
  call: CallDto,
  caller: UserDto,
  receiver: UserDto
): ReactElement => {
  let callExplanationText: string;
  const {
    originatorConnectionLogs: callerLogs = [],
    receiverConnectionLogs: receiverLogs = [],
  } = call;
  const allLogs = [
    ...(receiverLogs || []).map(log => ({
      user: receiver,
      role: 'reciever',
      ...log,
    })),
    ...(callerLogs || []).map(log => ({
      user: caller,
      role: 'caller',
      ...log,
    })),
  ];

  const sortedLogs = allLogs.sort((a, b) => {
    return a.timestamp === b.timestamp
      ? a.createdAt - b.createdAt
      : a.timestamp - b.timestamp;
  });

  if ((receiverLogs || []).length === 0 && call.status === CallStatus.FAILED) {
    callExplanationText =
      'Unable to reach Guide. Calls like this typically occur when there is little to no cellular connection, or the device is in "Airplane mode" or set to "Do not Disturb"';
  }

  if (sortedLogs.length === 0) {
    return null;
  }

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      }}
    >
      <table style={{ width: '75%' }}>
        <tbody>
          {sortedLogs.map(log => {
            const userName = log.user ? log.user.firstName : 'Unknown';
            let text: string;

            if (log.event === 'status_updated') {
              switch (log.status) {
                case 'initiated':
                  text =
                    log.role === 'receiver'
                      ? `Call received from ${caller?.firstName || 'Unknown'}`
                      : `Call sent to ${receiver?.firstName || 'Unknown'}`;
                  break;
                case 'completed':
                  text = `Call ended for ${userName}`;
                  break;
                case 'no-answer':
                  text = `${userName} never answered the call`;
                  break;
                case 'failed':
                  text = `An error occurred for ${userName}'s connection`;
                  break;
                case 'canceled':
                  text = `${userName}'s connection was canceled`;
                  break;
                case 'busy':
                  text = `${userName}'s line is gave a busy signal`;
                  break;
                case 'ringing':
                  text = `${userName}'s phone is ringing`;
                  break;
                case 'in-progress':
                  text = `${userName} has joined`;
                  break;
              }
            } else if (log.event === 'dial_another_connection') {
              return null;
            } else if (log.event === 'answered') {
              text = `${userName} answered call`;
            } else if (log.event === 'accepted') {
              text = `${userName} accepted call`;
            } else if (log.event === 'declined') {
              text = `${userName} declined call`;
            } else if (log.event === 'voicemail_initiated') {
              text = `Forwarding to ${userName}'s voicemail`;
            } else if (log.event === 'voicemail_started') {
              text = `Leaving a voicemail for ${userName}`;
            } else if (log.event === 'voicemail_saved') {
              text = `Voicemail was left for ${userName} `;
            } else if (log.event === 'call_recorded') {
              text = `Call recording saved`;
            } else {
              text = `${userName} - ${log.event}  ${
                log.status ? `(${log.status})` : ''
              }`;
            }

            return (
              <tr key={log.id}>
                <td style={{ paddingRight: '20px', verticalAlign: 'top' }}>
                  <strong>
                    {DateTime.fromMillis(log.timestamp).toFormat('h:mm:s a')}
                  </strong>
                </td>
                <td>{text}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {callExplanationText && (
        <div
          style={{
            marginTop: 16,
            width: '75%',
            padding: 12,
            backgroundColor: 'white',
            borderRadius: 4,
          }}
        >
          <div
            style={{
              flex: 'flex-start',
              flexDirection: 'row',
              justifyContent: 'center',
              paddingBottom: 4,
            }}
          >
            <InfoOutlined sx={{ height: 16, width: 16 }} />
            <Typography style={{ paddingLeft: 8 }} variant="bodyMedium">
              Potential Explanation
            </Typography>
          </div>
          <Typography variant="bodySmall">{callExplanationText}</Typography>
        </div>
      )}
    </div>
  );
};
